import React from 'react';
import { FONT_WEIGHT, type ThemeTokens } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';

// ─── NumberCell ───────────────────────────────────────────────────────────────

/** 숫자 칸의 종류 — 단위는 칸이 아니라 열 머리에 둔다(`numberHeader`, 가격 목록 개편 D3). */
export type NumberKind = 'won' | 'count' | 'percent';
/** 값이 없을 때(null · undefined)의 뜻 — 수집 전(`미수집`)과 해당 없음(`-`)을 다르게 그린다(개편 D4). */
export type NumberMissing = 'uncollected' | 'na';

/** 값이 없을 때 보이는 글자. `0` 은 값이 있는 것(수집했는데 0) — 이 글자를 쓰지 않는다. */
export const NUMBER_UNCOLLECTED_LABEL = '미수집';
export const NUMBER_NA_LABEL = '-';

const UNIT: Record<NumberKind, string> = { won: '원', count: '건', percent: '%' };

/**
 * 숫자 → 글자(색 없이). 표 밖(휴대폰 카드 · 툴팁 · 확인 창 문장)에서도 같은 규칙을 쓰게 따로 둔다.
 * - null · undefined → `미수집`(missing='uncollected', 기본) 또는 `-`(missing='na')
 * - 0 → `0` (수집했는데 0 — 미수집과 다르다)
 * - 천 단위 쉼표, percent 는 반올림한 정수 + `%`(퍼센트는 칸 안에서도 단위를 붙인다 — 보조줄이 많아서)
 * - signed 면 양수 앞에 `+`, 음수는 `−`(빼기 기호) 대신 `-`(복사 · 검색 호환)
 * - withUnit 이면 `원` · `건` 을 붙인다(열 머리에 단위가 없는 문장 속 숫자용)
 */
export function formatNumber(
  value: number | null | undefined,
  opts: { kind?: NumberKind; missing?: NumberMissing; signed?: boolean; withUnit?: boolean } = {},
): string {
  const { kind = 'won', missing = 'uncollected', signed = false, withUnit = false } = opts;
  if (value == null || Number.isNaN(value)) return missing === 'na' ? NUMBER_NA_LABEL : NUMBER_UNCOLLECTED_LABEL;
  const n = kind === 'percent' ? Math.round(value) : value;
  const sign = signed && n > 0 ? '+' : '';
  const body = n.toLocaleString('ko-KR');
  const unit = kind === 'percent' || withUnit ? UNIT[kind] : '';
  return `${sign}${body}${unit}`;
}

/**
 * 열 머리 — `네이버 가격 (원)`. 단위는 따로 감싼 글자라 열 이름 글자(`네이버 가격`)는 그대로 남는다(찾기 · 테스트 호환).
 * 퍼센트 열은 단위를 칸에 두므로 머리는 이름만.
 */
export const numberHeader = (label: React.ReactNode, kind: NumberKind = 'won'): React.ReactElement => (
  <>
    {label}
    {kind !== 'percent' && <span style={{ fontWeight: FONT_WEIGHT.regular }}>{` (${UNIT[kind]})`}</span>}
  </>
);

/** 숫자 칸 공통 모양 — 오른쪽 정렬 · 고정폭 숫자 · 줄바꿈 없음(자릿수가 다른 줄이 세로로 맞게, 개편 P2). */
export const NUMBER_CELL_STYLE: React.CSSProperties = {
  display: 'inline-block', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
};

/** signed 값의 색 — 오름은 success, 내림은 danger, 0 은 보조 글자. 색만으로 구분하지 않게 ▲▼ 를 같이 쓴다. */
const signedColor = (t: ThemeTokens, n: number): string =>
  n > 0 ? t.success : n < 0 ? t.danger : t.textSecondary;

export interface NumberCellProps {
  value: number | null | undefined;
  kind?: NumberKind;
  /** 값이 없을 때의 뜻. 기본 'uncollected'(`미수집`). 요청 불가 줄의 변경 예정가처럼 원래 없는 값은 'na'(`-`). */
  missing?: NumberMissing;
  /** 부호(+/-)와 ▲▼ 를 붙이고 오름 · 내림 색을 쓴다(가격 차이 · 변동률). */
  signed?: boolean;
  /** 흐린 보조 숫자(취소선 전 값 · 참고 값). */
  muted?: boolean;
  bold?: boolean;
  /** 값이 있을 때의 글자색(예: 매장 가격 강조 accent). signed 색보다 우선. */
  color?: string;
  /** 칸 안에 단위를 붙인다 — 열 머리에 단위가 없는 곳(휴대폰 카드 등). */
  withUnit?: boolean;
  fontSize?: number;
  title?: string;
  testId?: string;
}

/**
 * 가격 · 건수 · 비율 칸 하나(디자인 시스템 DS-1, 가격 목록 개편 PR 1). 미수집 · 0 · 해당 없음을 구분하고,
 * 오른쪽 정렬 · tabular-nums 로 자릿수를 세로로 맞춘다. 단위 `원` 은 열 머리(`numberHeader`)로 올린다.
 */
export const NumberCell: React.FC<NumberCellProps> = ({
  value, kind = 'won', missing = 'uncollected', signed = false, muted = false, bold = false,
  color, withUnit = false, fontSize, title, testId,
}) => {
  const t = useThemeTokens();
  const has = value != null && !Number.isNaN(value);
  const text = formatNumber(value, { kind, missing, signed, withUnit });
  // 미수집은 읽어야 하는 글자라 textMuted(4.5:1), 해당 없음 '-' 는 자리 표시라 textEmpty(3:1).
  const fg = !has
    ? (missing === 'na' ? t.textEmpty : t.textMuted)
    : color ?? (signed ? signedColor(t, value as number) : muted ? t.textMuted : t.textPrimary);
  const arrow = has && signed && (value as number) !== 0 ? ((value as number) > 0 ? '▲' : '▼') : '';
  return (
    <span
      data-testid={testId}
      data-number-state={has ? ((value as number) === 0 ? 'zero' : 'value') : missing}
      title={title}
      style={{
        ...NUMBER_CELL_STYLE, color: fg, fontSize,
        fontWeight: has && bold ? FONT_WEIGHT.bold : undefined,
      }}
    >
      {arrow && <span aria-hidden style={{ marginRight: 2 }}>{arrow}</span>}
      {text}
    </span>
  );
};
